import { useState, useEffect, useCallback } from "react";
import useSWR from 'swr';
import jsPDF from 'jspdf';
import TabPageLayout, { PageLoader, DataCard, EmptyState } from "./TabPageLayout";
import DataTable from "./DataTable";
import FilterChips from "./FilterChips";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import {
  Activity,
  Download,
  Search,
  RefreshCw,
  LogIn,
  LogOut,
  Eye,
  FileText,
  FileSpreadsheet,
  Shield,
  Database,
  Settings,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ClipboardList,
  X
} from "lucide-react";
import { BACKEND_URL } from '../config/api';
import axios from "axios";
import { toast } from "sonner";

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const fetcher = async (url) => {
  const token = localStorage.getItem("jwtToken");
  const response = await axios.get(url, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

// v2 audit actions are lowercase snake_case (login_success, otp_sent, org_approved, ...)
const getActionIcon = (action = '') => {
  const a = action.toLowerCase();
  if (a.includes('fail') || a.includes('blocked')) return <Shield className="w-4 h-4 text-red-600" />;
  if (a.includes('otp') || a.includes('2fa') || a.includes('pin')) return <Shield className="w-4 h-4 text-blue-600" />;
  if (a.includes('login') || a.includes('logout')) return a.includes('logout') ? <LogOut className="w-4 h-4 text-gray-600" /> : <LogIn className="w-4 h-4 text-green-600" />;
  if (a.includes('export')) return <Download className="w-4 h-4 text-blue-600" />;
  if (a.includes('org')) return <Database className="w-4 h-4 text-gray-600" />;
  if (a.includes('admin') || a.includes('role')) return <Settings className="w-4 h-4 text-gray-600" />;
  if (a.includes('tab_viewed') || a.includes('view')) return <Eye className="w-4 h-4 text-gray-400" />;
  return <Activity className="w-4 h-4 text-gray-400" />;
};

const getActionBadgeColor = (action = '') => {
  const a = action.toLowerCase();
  if (a.includes('fail') || a.includes('blocked') || a.includes('suspend') || a.includes('delete')) return 'bg-red-50 text-red-700';
  if (a.includes('success') || a.includes('verified') || a.includes('approved') || a.includes('activated') || a.includes('created')) return 'bg-green-50 text-green-700';
  if (a.includes('export') || a.includes('otp') || a.includes('support_login')) return 'bg-blue-50 text-blue-700';
  if (a.includes('tab_viewed')) return 'bg-gray-100 text-gray-500';
  return 'bg-gray-100 text-gray-600';
};

const getShortActionLabel = (action = '') => {
  const labels = {
    login_success: 'Login',
    login_failed: 'Login Failed',
    logout: 'Logout',
    otp_sent: '2FA Sent',
    otp_failed: '2FA Failed',
    pin_failed: 'Wrong PIN',
    tab_viewed: 'Viewed Tab',
    org_support_login: 'Support Login',
    activity_logs_exported: 'Export',
    report_exported: 'Export',
  };
  return labels[action.toLowerCase()] || action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Unknown';
};

const ACTION_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'login', label: 'Logins' },
  { value: 'otp', label: '2FA' },
  { value: 'pin', label: 'PIN' },
  { value: 'org', label: 'Accounts' },
  { value: 'admin', label: 'Team' },
  { value: 'export', label: 'Exports' },
  { value: 'tab_viewed', label: 'Navigation' },
];

const ActivityLog = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(0);
  const [exporting, setExporting] = useState(false);
  const limit = 50;

  const debouncedSearch = useDebounce(searchTerm, 300);

  const buildFilterParams = useCallback(() => {
    const params = new URLSearchParams();
    if (actionFilter !== "all") params.append("action", actionFilter);
    if (debouncedSearch) params.append("username", debouncedSearch);
    if (startDate) params.append("start_date", startDate);
    if (endDate) params.append("end_date", endDate);
    return params;
  }, [actionFilter, debouncedSearch, startDate, endDate]);

  const buildQueryString = useCallback(() => {
    const params = buildFilterParams();
    params.append("limit", limit.toString());
    params.append("offset", (page * limit).toString());
    return params.toString();
  }, [buildFilterParams, page]);

  const { data, error, isLoading, mutate } = useSWR(
    `${BACKEND_URL}/api/v2/super-admin/activity-logs?${buildQueryString()}`,
    fetcher,
    {
      revalidateOnFocus: true,
      refreshInterval: 60000,
      dedupingInterval: 10000,
      onError: () => toast.error("Failed to load activity logs")
    }
  );

  const logs = data?.logs || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / limit);

  useEffect(() => {
    setPage(0);
  }, [actionFilter, debouncedSearch, startDate, endDate]);

  const dateRangeLabel = startDate && endDate ? `${startDate} → ${endDate}` : startDate ? `since ${startDate}` : endDate ? `until ${endDate}` : 'all time';

  const exportLogs = async (format) => {
    setExporting(true);
    try {
      const token = localStorage.getItem("jwtToken");
      const params = buildFilterParams();
      params.append("format", format === 'pdf' ? 'json' : format);
      const url = `${BACKEND_URL}/api/v2/super-admin/activity-logs/export?${params.toString()}`;

      if (format === 'csv') {
        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        });
        const blobUrl = URL.createObjectURL(response.data);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `activity-logs-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(blobUrl);
        toast.success("Activity logs exported as CSV");
      } else if (format === 'json') {
        const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
        const blob = new Blob([JSON.stringify(response.data.logs, null, 2)], { type: 'application/json' });
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `activity-logs-${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        URL.revokeObjectURL(blobUrl);
        toast.success("Activity logs exported as JSON");
      } else {
        // PDF — built client-side from the filtered export data
        const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
        const rows = response.data.logs || [];
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        let y = 16;
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.text('BookingFlows — Log book', pageWidth / 2, y, { align: 'center' });
        y += 6;
        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(110);
        doc.text(`${dateRangeLabel}  •  ${rows.length} events  •  generated ${new Date().toLocaleString()}`, pageWidth / 2, y, { align: 'center' });
        y += 8;
        doc.setTextColor(0);
        doc.setFontSize(8);
        doc.setFont(undefined, 'bold');
        doc.text('Time', 14, y);
        doc.text('Action', 52, y);
        doc.text('User', 96, y);
        doc.text('Details', 124, y);
        y += 2;
        doc.setDrawColor(200);
        doc.line(14, y, pageWidth - 14, y);
        y += 5;
        doc.setFont(undefined, 'normal');
        rows.forEach((log) => {
          if (y > 282) {
            doc.addPage();
            y = 16;
          }
          const ts = log.created_at ? new Date(log.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
          doc.text(String(ts).substring(0, 22), 14, y);
          doc.text(String(log.action || '').substring(0, 26), 52, y);
          doc.text(String(log.user_name || '').substring(0, 16), 96, y);
          doc.text(String(log.description || '').substring(0, 52), 124, y);
          y += 4.5;
        });
        doc.save(`activity-logs-${new Date().toISOString().split('T')[0]}.pdf`);
        toast.success("Activity logs exported as PDF");
      }
    } catch (err) {
      toast.error("Failed to export logs");
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const logColumns = [
    { key: 'time', label: 'Time', render: (log) => <span className="text-gray-500 whitespace-nowrap">{formatDate(log.created_at)}</span> },
    { key: 'action', label: 'Action', render: (log) => (
      <div className="flex items-center gap-2">
        {getActionIcon(log.action)}
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${getActionBadgeColor(log.action)}`}>
          {getShortActionLabel(log.action)}
        </span>
      </div>
    ) },
    { key: 'user', label: 'User', render: (log) => <span className="font-medium text-gray-900">{log.user_name || '—'}</span> },
    { key: 'ip', label: 'IP Address', render: (log) => <span className="text-gray-500 font-mono text-xs">{log.extra_data?.ip || '—'}</span> },
    { key: 'details', label: 'Details', render: (log) => (
      <span className="text-gray-500 text-xs block max-w-xs truncate" title={log.description || ''}>
        {log.description || '—'}
      </span>
    ) },
  ];

  return (
    <TabPageLayout
      title="Log book"
      subtitle={`${total.toLocaleString()} events (${dateRangeLabel}) • Auto-refreshes every 60s`}
      actions={
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => mutate()}
            className="h-9"
            aria-label="Refresh"
            data-testid="activity-refresh-button"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-9" disabled={exporting} data-testid="activity-export-button">
                <Download className="w-4 h-4 mr-1" />Export<ChevronDown className="w-3 h-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => exportLogs('pdf')} data-testid="activity-export-pdf">
                <FileText className="w-4 h-4 mr-2" />PDF Report
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportLogs('csv')} data-testid="activity-export-csv">
                <FileSpreadsheet className="w-4 h-4 mr-2" />CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportLogs('json')} data-testid="activity-export-json">
                <FileText className="w-4 h-4 mr-2" />JSON
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Filters */}
        <DataCard>
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by user..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-10"
                  data-testid="activity-search-input"
                />
              </div>
              <div className="flex items-end gap-2">
                <div>
                  <Label className="text-xs text-gray-500">From</Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="h-10 mt-1 w-[150px]"
                    data-testid="activity-start-date"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">To</Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="h-10 mt-1 w-[150px]"
                    data-testid="activity-end-date"
                  />
                </div>
                {(startDate || endDate) && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-10"
                    onClick={() => { setStartDate(""); setEndDate(""); }}
                    aria-label="Clear dates"
                    data-testid="activity-clear-dates"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
            <FilterChips
              options={ACTION_FILTERS}
              value={actionFilter}
              onChange={setActionFilter}
              testIdPrefix="activity-filter"
            />
          </div>
        </DataCard>

        {/* Logs Table */}
        <DataCard>
          {isLoading ? (
            <PageLoader minHeight="h-48" />
          ) : error ? (
            <EmptyState
              icon={<Activity />}
              title="Failed to load logs"
              description="There was an error loading activity logs. Please try again."
              action={
                <Button onClick={() => mutate()} className="bg-blue-600 hover:bg-blue-700 text-white h-10">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Retry
                </Button>
              }
            />
          ) : logs.length === 0 ? (
            <EmptyState
              icon={<ClipboardList />}
              title="No activity logs"
              description={searchTerm || actionFilter !== 'all' || startDate || endDate
                ? "No logs match your current filters. Try adjusting your search or date range."
                : "Activity logs will appear here as events occur."
              }
            />
          ) : (
            <>
              <DataTable
                columns={logColumns}
                rows={logs}
                rowKey="id"
                minWidth={600}
                testId="activity-logs-table"
              />

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-gray-200 mt-4">
                  <p className="text-sm text-gray-500">
                    Showing {(page * limit + 1).toLocaleString()} - {Math.min((page + 1) * limit, total).toLocaleString()} of {total.toLocaleString()}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(0, p - 1))}
                      disabled={page === 0}
                      className="h-9"
                      data-testid="activity-prev-page"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-gray-600 min-w-[100px] text-center">
                      Page {page + 1} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                      disabled={page >= totalPages - 1}
                      className="h-9"
                      data-testid="activity-next-page"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </DataCard>
      </div>
    </TabPageLayout>
  );
};

export default ActivityLog;
